import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function EmailCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Connecting your Gmail account...");

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (error) {
        setStatus("error");
        setMessage(`Authorization failed: ${error}`);
        toast.error("Gmail authorization was denied");
        setTimeout(() => navigate("/email-tracking"), 3000);
        return;
      }

      if (!code) {
        setStatus("error");
        setMessage("No authorization code received");
        toast.error("No authorization code received");
        setTimeout(() => navigate("/email-tracking"), 3000);
        return;
      }

      try {
        // Exchange the code for tokens
        const response = await axios.post(
          `${API}/email/callback`,
          null,
          {
            params: { code, state },
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          setStatus("success");
          setMessage(`Connected successfully: ${response.data.email}`);
          toast.success("Gmail connected successfully!");
          setTimeout(() => navigate("/email-tracking"), 2000);
        } else {
          throw new Error("Connection failed");
        }
      } catch (error) {
        console.error("OAuth callback error:", error);
        setStatus("error");
        setMessage(error.response?.data?.detail || "Failed to connect Gmail");
        toast.error("Failed to connect Gmail");
        setTimeout(() => navigate("/email-tracking"), 3000);
      }
    };

    if (token) {
      handleCallback();
    }
  }, [searchParams, token, navigate]);

  return (
    <div className="min-h-screen bg-[#F3F1EC] flex items-center justify-center">
      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100 max-w-md w-full text-center">
        {status === "processing" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-[#A3C3D8] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#2D3748] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Connecting Gmail
            </h2>
            <p className="text-[#94A3B8]">{message}</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#2D3748] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Connected Successfully!
            </h2>
            <p className="text-[#94A3B8]">{message}</p>
            <p className="text-sm text-[#94A3B8] mt-4">Redirecting to Email Tracking...</p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#2D3748] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Connection Failed
            </h2>
            <p className="text-[#94A3B8]">{message}</p>
            <p className="text-sm text-[#94A3B8] mt-4">Redirecting back...</p>
          </>
        )}
      </div>
    </div>
  );
}
